import { Injectable } from '@angular/core';
import { GitQuery, GitService } from '@y42/git';
import { Y42AssetStatus, Y42Metadata } from '@y42/json-schema';
import { Y42GitFolders, Y42GitSourceFolder } from '../../../git';

@Injectable({ providedIn: 'root' })
export class ExportTemplateService {
  constructor(private readonly gitService: GitService, private readonly gitQuery: GitQuery) {}

  async exportTemplateForAsset(path: string): Promise<Record<string, unknown>> {
    const featureDirectories = new Set(Object.values(Y42GitFolders).map((dir) => `${Y42GitSourceFolder}${dir}`));
    const templateWithDeps: Record<string, unknown> = {};
    const visitedPaths = new Set<string>();
    const itemsToVisit: Array<{ path: string; shouldSkipDir?: boolean }> = [{ path }];

    while (itemsToVisit.length > 0) {
      const currentItem = itemsToVisit.pop();

      if (!currentItem) {
        break;
      }

      if (visitedPaths.has(currentItem.path)) {
        continue;
      }

      visitedPaths.add(currentItem.path);

      const item = this.gitQuery.getTreeItemAtPath(currentItem.path);

      if (!item) {
        continue;
      }

      // Export parent items till the feature directory.
      const parentItem = this.gitQuery.getTreeItemAtPath(currentItem.path.replace(/\/[^/]+$/, ''));

      if (parentItem && !featureDirectories.has(parentItem.path)) {
        // Skip going into the directories when going up te tree.
        itemsToVisit.push({ path: parentItem.path, shouldSkipDir: true });
      }

      if (item.type === 'dir') {
        for (const gitTreeItem of Object.values(item.children ?? {})) {
          if (currentItem.shouldSkipDir && gitTreeItem.type === 'dir') {
            continue;
          }

          itemsToVisit.push({ path: gitTreeItem.path });
        }
      } else {
        // Skip credentials.
        if (item.name.endsWith('.enc')) {
          continue;
        }

        const asset = item.name.endsWith('.json')
          ? await this.gitService.readJSON(item.path)
          : await this.gitService.readFile(item.path);

        if (item.name.endsWith('metadata.json')) {
          templateWithDeps[item.path] = clearMetadataFile(asset as Y42Metadata);
        } else {
          templateWithDeps[item.path] = asset;
        }

        const assetDependencies = getAssetDependencies(asset);

        itemsToVisit.push(...assetDependencies.map((dep) => ({ path: dep })));
      }
    }

    console.debug('[Exported template]', templateWithDeps);

    // Sort assets alphabetically.
    return Object.fromEntries(Object.entries(templateWithDeps).sort(([key1], [key2]) => key1.localeCompare(key2)));
  }

  downloadFile(file: File) {
    const linkElement = document.createElement('a');

    linkElement.style.display = 'none';
    linkElement.href = URL.createObjectURL(file);
    linkElement.download = file.name;

    document.body.appendChild(linkElement);

    linkElement.click();

    document.body.removeChild(linkElement);
  }
}

// Alternatively, the data lineage should also do the work
// but not in all areas (e.g. it will not work for our themes).
function getAssetDependencies(assetPart: unknown): string[] {
  // We assume that the asset links other assets only by full paths.
  // False positive values should not be a problem as files not existing
  // in git will be filter out.
  if (typeof assetPart === 'string' && assetPart.includes(Y42GitSourceFolder)) {
    return assetPart.match(/\bsrc\/[\w()/ .-]+/g) ?? [];
  }

  if (Array.isArray(assetPart)) {
    return assetPart.map(getAssetDependencies).flat();
  }

  if (typeof assetPart === 'object' && assetPart !== null) {
    return Object.values(assetPart).map(getAssetDependencies).flat();
  }

  return [];
}

function clearMetadataFile(metadataFile: Y42Metadata): Y42Metadata {
  return {
    ...metadataFile,
    tags: [],
    owners: {
      user_ids: [],
      team_ids: [],
    },
    experts: {
      user_ids: [],
      team_ids: [],
    },
    watchers: {
      user_ids: [],
      team_ids: [],
    },
    asset_status: Y42AssetStatus.NoStatus,
  };
}
